/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import MedicationsHeader from "./Header/MedicationsHeader";
import SaveMedicationPopup from "./Popup/SaveMedicationPopup";

const MedicationsListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [medicationsList, setMedicationsList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedMedicationIds, setSelectedMedicationIds] = useState([]);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // get all medications
  const getAllMedications = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_MEDICINE + `?token=${token}`;
      const response = await getData(requestUrl);
      setIsLoading(false);
      if (response.status) {
        setMedicationsList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // bulk update medications
  const updateBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeUpdate = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeUpdate,
        };

        let requestURL =
          url.API_BASE_URL + url.API_UPDATE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedMedicationIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one medication");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // bulk delete medications
  const deleteBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeDelete = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL + url.API_DELETE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setMedicationsList((prevList) =>
              prevList.filter(
                (item) => !selectedMedicationIds.includes(item._id)
              )
            );
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one medication");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // After any action
  const afterActionClose = (data = []) => {
    setMedicationsList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllMedications();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveMedicationOffCanvas"
            aria-controls="saveMedicationOffCanvas"
            onClick={() => {
              setSelectedMedicationId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Start Date",
        accessorKey: "medicinestartdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "End Date",
        accessorKey: "medicineenddate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Recommended by",
        accessorKey: "referredpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.referredpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.referredpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.referredpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.referredpersonname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "For",
        accessorKey: "assignedpersonname",
        size: 200,
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Direction / Usage",
        accessorKey: "comments",
        size: 200,
        Cell: ({ row }) => (
          <ul className="list-style-none p-0 m-0 d-flex flex-column gap-1">
            {row.original?.usagelist?.map((usage, index) => (
              <li key={index} className="text-gray fw-semibold">
                <span className="material-symbols-outlined icon-md text-danger me-1">
                  notifications_active
                </span>
                Dose {index + 1} : {usage.dose}mg at{" "}
                <span className="text-primary">{usage.time}</span>
              </li>
            ))}
          </ul>
        ),
      },
    ],
    []
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    if (selectedIdsArray.length > 0) {
      setSelectedMedicationIds(selectedIdsArray);
    } else {
      setSelectedMedicationIds([]);
    }
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT")
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Medications")}
          />
          <div className="container-fluid px-lg-5">
            <MedicationsHeader
              selectedItemIds={selectedMedicationIds}
              updateBulkHandler={updateBulkMedicationsHandler}
              reloadList={getAllMedications}
            />

            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={medicationsList}
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* popup and modals */}
          <SaveMedicationPopup
            selectedMedicationId={selectedMedicationId}
            setSelectedMedicationId={setSelectedMedicationId}
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName="medicine"
            afterModalClose={afterActionClose}
            selectedIds={selectedMedicationIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedMedicationIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkMedicationsHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedMedicationIds([]);
            }}
            moduleName={t("Medications")}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default MedicationsListBody;
